<script lang="ts" setup>
	import type { ClassNameValue } from 'tailwind-merge'
	import type { IconLibProps } from '~/components/Tools/icon.vue'

	export type PricingCardPattern = {
		name?: {
			text: string | null
			ui?: PricingCardTitleContext
		}
		description: {
			text: string
			ui?: PricingCardDescription
		}
		price?: {
			text: string | number
			ui?: string
		}
		tag?: {
			text?: string[]
			ui?: string
		}
		benefit_highlight?: Array<{
			text: string
			notAllowed?: boolean
		}>
		dedicated_benfit?: PricingCardPattern['benefit_highlight']
		individual_benefit?: PricingCardPattern['benefit_highlight']
		card_icon?: {
			onhover?: IconLibProps
			default?: IconLibProps
		}
		actions?: {
			text: string
			ui?: string
		}
	}
	export type PricingCardTitleContext = Partial<{
		base: unknown
		font: ClassNameValue
		size: ClassNameValue
		transition: ClassNameValue
		hover: ClassNameValue
		text: ClassNameValue
	}>
	export type PricingCardDescription = Partial<{
		base: ClassNameValue
		size: ClassNameValue
		font: ClassNameValue
		text: ClassNameValue
		width: ClassNameValue
	}>
	const descriptionConfig: PricingCardDescription = {
		base: '',
		size: 'text-sm ',
		font: 'font-medium',
		text: 'text-gray-600',
		width: 'max-w-[310px]'
	}
	const titleConfig: PricingCardTitleContext = {
		base: ' capitalize',
		font: 'font-semibold',
		size: 'sm:text-xl text-lg',
		hover: 'group-hover:scale-105 transition-transform',
		transition: 'custom-transition-2',
		text: 'text-main  text-xl sm:text-lg'
	}

	const configClass = {
		wrapper: {
			base: 'relative flex-none flex flex-col justify-between item-start',
			rounded: 'rounded-lg',
			bg: 'bg-white',
			border: 'border',
			transition: 'custom-transition-2',
			hover: 'hover:bg-purple-50 hover:border-purple-300',
			height: 'min-h-[590px]',
			width: 'max-w-[380px] sm:min-w-[380px] min-w-[325.36px] w-full',
			padding: 'p-5'
		},
		heading: {
			border: 'border-b border-gray-300',
			spacer: 'space-y-4',
			paddingBlock: 'sm:py-5 ',
			base: ''
		},
		list: {
			base: '  z-10 ',
			items: 'flex items-center gap-x-3 ',
			iconHover: 'group-hover:fill-main ',
			iconTransition: 'custom-transition-3',
			text: 'text-gray-500',
			size: 'sm:text-base text-sm',
			height: 'min-h-[325px]',
			space: 'space-y-3',
			padding: 'sm:py-5',
			overflow: 'overflow-hidden',
			margin: 'mb-auto mt-3 sm:mt-0',
			position: 'relative'
		},
		actionBtn: {
			base: 'underline-2',
			padding: 'pt-10  pl-0',
			zIndex: 'z-10',
			hover: 'group-hover:text-main ',
			text: 'sm:text-lg text-gray-700',
			display: 'inline-flex'
		}
	}

	const { wrapper, list, heading, actionBtn } = configClass
	defineProps<
		PricingCardPattern & {
			ui?: {
				wrapper?: Partial<{
					base: ClassNameValue
					rounded: ClassNameValue
					bg: ClassNameValue
					border: ClassNameValue
					transition: ClassNameValue
					hover: ClassNameValue
					height: ClassNameValue
					width: ClassNameValue
					padding: ClassNameValue
				}>
				heading?: Partial<{
					border: ClassNameValue
					spacer: ClassNameValue
					paddingBlock: ClassNameValue
					base: ClassNameValue
					name: Partial<PricingCardTitleContext>
					description: Partial<PricingCardDescription>
				}>
				list?: Partial<{
					base: ClassNameValue
					items: ClassNameValue
					iconHover: ClassNameValue
					iconTransition: ClassNameValue
					text: ClassNameValue
					size: ClassNameValue
					iconBase: ClassNameValue
					iconHeight: ClassNameValue
					height: ClassNameValue
					space: ClassNameValue
					padding: ClassNameValue
					overflow: ClassNameValue
					margin: ClassNameValue
					position: ClassNameValue
				}>
				actionBtn?: Partial<{
					base: ClassNameValue
					padding: ClassNameValue
					zIndex: ClassNameValue
					text: ClassNameValue
					display: ClassNameValue
					hover?: ClassNameValue
				}>
			}
			noIcon?: boolean
			label?: string
		}
	>()
	defineEmits<{
		(event: 'learnMore'): void
	}>()
</script>

<template>
	<div
		class="group"
		:class="[
			ui?.wrapper?.base ?? wrapper.base,
			ui?.wrapper?.bg ?? wrapper.bg,
			ui?.wrapper?.border ?? wrapper.border,
			ui?.wrapper?.height ?? wrapper.height,
			ui?.wrapper?.hover ?? wrapper.hover,
			ui?.wrapper?.padding ?? wrapper.padding,
			ui?.wrapper?.rounded ?? wrapper.rounded,
			ui?.wrapper?.transition ?? wrapper.transition,
			ui?.wrapper?.width ?? wrapper.width
		]"
	>
		<div
			:class="[
				ui?.heading?.base ?? heading?.base,
				ui?.heading?.border ?? heading.border,
				ui?.heading?.paddingBlock ?? heading.paddingBlock,
				ui?.heading?.spacer ?? heading.spacer,
				'relative z-[70]'
			]"
		>
			<h3
				:class="[
					ui?.heading?.name?.base ?? name?.ui?.base ?? titleConfig?.base,
					ui?.heading?.name?.font ?? name?.ui?.font ?? titleConfig?.font,
					ui?.heading?.name?.hover ?? name?.ui?.hover ?? titleConfig?.hover,
					ui?.heading?.name?.size ?? name?.ui?.size ?? titleConfig?.size,
					ui?.heading?.name?.text ?? name?.ui?.text ?? titleConfig?.text,
					ui?.heading?.name?.transition ?? name?.ui?.transition ?? titleConfig?.transition
				]"
			>
				<slot name="name">
					<span v-html="name?.text"></span>
				</slot>
			</h3>
			<div
				:class="[
					ui?.heading?.description?.base ?? description?.ui?.base ?? descriptionConfig?.base,
					ui?.heading?.description?.font ?? description?.ui?.font ?? descriptionConfig?.font,
					ui?.heading?.description?.size ?? description?.ui?.size ?? descriptionConfig?.size,
					ui?.heading?.description?.text ?? description?.ui?.text ?? descriptionConfig?.text,
					ui?.heading?.description?.width ?? description?.ui?.width ?? descriptionConfig?.width
				]"
			>
				<slot name="description">
					<p v-html="description?.text" />
				</slot>
			</div>
			<p class="text-sm font-medium text-gray-600">
				<slot name="label">
					{{ label }}
				</slot>
			</p>

			<div
				v-for="(t, index) in tag?.text ?? []"
				:key="'tag-' + index"
				class="custom-transition-2 bg-purple-5 mx-1 inline-flex rounded-[20px] p-[6px_10px] text-xs font-medium text-purple-500 first:ml-0 group-hover:bg-purple-500 group-hover:text-white sm:p-[10px_16px]"
				:class="tag?.ui ?? ''"
			>
				{{ t }}
			</div>
		</div>

		<ul
			:class="[
				ui?.list?.base ?? list?.base,
				ui?.list?.space ?? list?.space,
				ui?.list?.height ?? list?.height,
				ui?.list?.padding ?? list?.padding,
				ui?.list?.overflow ?? list?.overflow,
				ui?.list?.margin ?? list?.margin,
				ui?.list?.position ?? list?.position
			]"
		>
			<li v-for="(benefit, index) in benefit_highlight" :key="index" :class="ui?.list?.items ?? list?.items">
				<slot name="list-icon" :benefit="benefit">
					<lazy-tools-icon
						v-if="!benefit.notAllowed"
						dir="fill"
						icon="check"
						:width="14"
						:height="14"
						:path-class="`${ui?.list?.iconHover ?? 'group-hover:fill-base-primary'} ${
							ui?.list?.iconTransition
						} ${ui?.list?.iconBase}`"
						svg-class="sm:h-[24px] sm:w-[24px] h-[16px] w-[16px]"
					/>
					<lazy-tools-icon
						v-else
						dir="fill"
						:width="14"
						:height="14"
						icon="not-allowed"
						:path-class="`${ui?.list?.iconHover ?? 'group-hover:fill-error-500'} ${
							ui?.list?.iconTransition
						} `"
						:svg-class="ui?.list?.iconHeight ?? 'sm:h-[24px] sm:w-[24px] h-[16px] w-[16px]'"
					/>
				</slot>
				<span :class="[ui?.list?.size ?? list?.size, ui?.list?.text ?? list?.text]">
					{{ benefit?.text ?? '' }}
				</span>
			</li>
		</ul>

		<div>
			<slot
				name="btn-group"
				:btn-group-class="[
					'group/item',
					ui?.actionBtn?.base ?? actionBtn?.base,
					ui?.actionBtn?.display ?? actionBtn?.display,
					ui?.actionBtn?.padding ?? actionBtn?.padding,
					ui?.actionBtn?.text ?? actionBtn?.text,
					ui?.actionBtn?.zIndex ?? actionBtn?.zIndex,
					ui?.actionBtn?.hover ?? actionBtn?.hover,
					'z-10 items-center gap-x-2'
				]"
			/>
		</div>
	</div>
</template>
